import React, { FC, useEffect, useMemo, useCallback, useState } from "react";
import moment from "moment";

import { CreateStripeSession } from "api";
import { useAccessToken } from "auth/utils/useAccessToken";
import { useGetAppOverviewQuery } from "generated/graphql";
import { getApiUrl } from "utils/getApiUrl";
import { getDefaultProduct, getProduct } from "utils/getProducts";
import { getCoupon } from "utils/getCoupons";

import { CheckoutStep, CheckoutStepProps } from "../../components/CheckoutStep";
import { OnboardingProps } from "../../models";

export interface CheckoutStepContainerProps
    extends Omit<
        CheckoutStepProps,
        "loading" | "user" | "onAcceptTermsOfService" | "onCheckout" | "app" | "createProps" | "product" | "coupon"
    > {
    /**
     * Detected CMS
     */
    cms?: string;
    /**
     * The partner
     */
    partner?: string;
    /**
     * The coupon id
     */
    couponId?: string;
    /**
     * The product Id
     */
    productId?: string;
    /**
     * Optional path to redirect to on successful checkout
     */
    successPath?: string;
    /**
     * App information
     */
    onboarding: OnboardingProps;
}

/**
 * V3 Checkout
 *
 * @param props
 * @returns
 */
export const CheckoutStepContainer: FC<CheckoutStepContainerProps> = (props) => {
    const { onboarding, cms, partner, productId, couponId } = props;

    const { appId, organizationId } = onboarding;

    const [loadingMessage, setLoadingMessage] = useState<string>();
    const [error, setError] = useState<{ error: string; message: string }>();
    const accessToken = useAccessToken();
    const api = getApiUrl();

    let product = getProduct(productId);
    if (!product) {
        product = getProduct(getDefaultProduct());
    }

    // let noTrial = typeof props.noTrial === "boolean" ? props.noTrial : true;
    const noTrial = true;

    // Only get coupon if couponId is provided and not empty
    const coupon = couponId && couponId !== "null" && couponId !== "undefined" ? getCoupon(couponId) : undefined;

    let successPath = props.successPath;
    if (!successPath) {
        successPath = `/setup/${appId}`;

        if (cms) {
            successPath += `&cms=${cms}`;
        }

        if (partner) {
            successPath += `&partner=${partner}`;
        }
    }

    const now = useMemo(() => moment(), []);
    const [start] = useState<Date>(now.clone().add(-1, "week").toDate());
    const [end] = useState<Date>(now.toDate());

    const [getAppOverview] = useGetAppOverviewQuery({
        pause: !appId,
        requestPolicy: "cache-and-network",
        variables: {
            appId,
            end,
            start,
        },
    });

    const { data, fetching } = getAppOverview;
    const name: string = data?.app?.name;

    /*
    useEffect(() => {
        ReactGA.event("onboarding_step_view", {
            step: "checkout",
        });
    }, []);
    */

    useEffect(() => {
        if (fetching) {
            setLoadingMessage("Pulling latest info...");
        } else {
            setLoadingMessage("");
        }
    }, [fetching]);

    const handleCheckout = useCallback(async () => {
        const session: CreateStripeSession = {
            appId,
            organizationId,
            prodId: productId,
            cancelPath: window.location.pathname + window.location.search,
            successPath,
            // Removing all trials for now
            noTrial: "true",
        };

        if (coupon && coupon.id !== "null") {
            session.couponId = coupon.id;
        }

        if (noTrial) {
            session.noTrial = "true";
        }

        if (productId === getDefaultProduct()) {
            session.noTrial = "true";
        }

        setLoadingMessage("Preparing final checkout...");

        // TODO: Update this to use getCheckoutUrl from "api"
        const url: string = await fetch(`${api}/stripe/checkout`, {
            method: "POST",
            headers: {
                "x-xapp-usertoken": accessToken,
                "content-type": "application/json",
            },
            body: JSON.stringify(session),
        })
            .then((res) => {
                return res.json();
            })
            .then((json) => {
                if (json.httpStatus === 200) {
                    return json.url;
                } else {
                    setError({ error: json.message, message: "We had trouble preparing checkout for you." });
                    return undefined;
                }
            })
            .catch((err: Error): undefined => {
                console.error(err);
                setError({ error: err.message, message: "We had trouble preparing checkout." });
                return undefined;
            });
        if (url) {
            // Is this ok to do?

            // fire custom event for tracking

            window.location.replace(url);
        } else {
            setLoadingMessage("");
        }
    }, [accessToken, api, appId, coupon, noTrial, organizationId, productId, successPath]);

    return (
        <CheckoutStep
            {...props}
            app={{ appId, organizationId, name }}
            createProps={onboarding}
            noTrial={noTrial}
            product={product}
            coupon={coupon}
            error={error}
            loading={!!loadingMessage || fetching}
            loadingMessage={loadingMessage}
            onCheckout={handleCheckout}
        />
    );
};
